// Reusable rounded card used as the outer container for every People
// Insights section. Provides a consistent header (icon + title + subtitle
// + optional action) and body padding.

import React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

export interface SectionCardProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Optional left-side icon (already coloured); rendered before the title. */
  icon?: React.ReactNode;
  /** Right-aligned action area (button, chip, link, etc). */
  action?: React.ReactNode;
  children: React.ReactNode;
  /** Forward extra MUI sx (e.g. column spans inside a grid). */
  sx?: SxProps<Theme>;
  /** Override inner body padding; defaults to 3 (24px). */
  bodyPadding?: number;
}

const SectionCard: React.FC<SectionCardProps> = ({
  title,
  subtitle,
  icon,
  action,
  children,
  sx,
  bodyPadding = 3,
}) => (
  <Paper
    elevation={0}
    sx={[
      {
        backgroundColor: '#fff',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)',
        overflow: 'hidden',
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      spacing={2}
      sx={{
        px: bodyPadding,
        pt: bodyPadding,
        pb: subtitle ? 0.5 : 1,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
        {icon}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.25 }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
      {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
    </Stack>
    <Box sx={{ px: bodyPadding, pb: bodyPadding, pt: 2 }}>{children}</Box>
  </Paper>
);

export default SectionCard;
